import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    CapabilityFieldControllerService,
    CapabilityFieldRecord,
} from '../../api/capability-service';
import { CapabilityFieldUpsertRequest } from '../../api/capability-service/model/capabilityFieldUpsertRequest';
import { PermissionCheckerService } from '../../services/permission/permission-checker.service';

export interface CapabilityFieldDialogData {
    code: string;
    version: string;
    mode: 'create' | 'edit';
    field: CapabilityFieldRecord | null;
}

/**
 * Capability-field add/edit dialog. Conditional fields by kind:
 *   Connector → connectorType
 *   Data      → dataType (string / number / enum / userdata)
 *       enum     → enumValuesJson (JSON array of strings)
 *       userdata → userdataType + userdataAttrFilterJson
 */
@Component({
    selector: 'app-capability-field-dialog',
    standalone: false,
    template: `
<div class="bw-dialog cap-field-dialog">
    <lib-page-header [screenName]="data.mode === 'create' ? 'New field' : 'Edit field'"
                     [title]="(data.field?.fieldName || form.get('fieldName')?.value || 'New field')"
                     backTo="">
        <mat-icon icon>{{ data.mode === 'create' ? 'add' : 'edit' }}</mat-icon>
        <ng-container subtitle>
            <span class="mono">{{ data.code }} {{ data.version }}</span>
        </ng-container>
        <ng-container actions>
            <button mat-icon-button (click)="cancel()" matTooltip="Cancel" aria-label="Cancel">
                <mat-icon>close</mat-icon>
            </button>
        </ng-container>
    </lib-page-header>

    <div class="bw-body">
        <form [formGroup]="form" class="form-grid">
            <mat-form-field appearance="outline">
                <mat-label>Field name</mat-label>
                <input matInput formControlName="fieldName"
                       [readonly]="data.mode === 'edit'"
                       placeholder="e.g. deviceIp" required />
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Role</mat-label>
                <mat-select formControlName="fieldRole" [disabled]="data.mode === 'edit'">
                    <mat-option value="INPUT">INPUT</mat-option>
                    <mat-option value="OUTPUT">OUTPUT</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Group (optional)</mat-label>
                <input matInput formControlName="groupName" placeholder="e.g. identity" />
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Kind</mat-label>
                <mat-select formControlName="fieldKind">
                    <mat-option value="Data">Data</mat-option>
                    <mat-option value="Connector" [disabled]="form.get('fieldRole')?.value === 'OUTPUT'">
                        Connector (INPUT only)
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
                <mat-label>Cardinality</mat-label>
                <mat-select formControlName="cardinality">
                    <mat-option value="single">single</mat-option>
                    <mat-option value="list">list</mat-option>
                </mat-select>
            </mat-form-field>

            <div class="checkbox-row">
                <mat-checkbox formControlName="required">Required</mat-checkbox>
            </div>

            <mat-form-field appearance="outline">
                <mat-label>Field order</mat-label>
                <input matInput type="number" formControlName="fieldOrder" min="0" />
            </mat-form-field>

            <!-- Connector-kind conditional -->
            <mat-form-field appearance="outline" *ngIf="form.get('fieldKind')?.value === 'Connector'"
                            class="conditional-field">
                <mat-label>Connector type</mat-label>
                <input matInput formControlName="connectorType" placeholder="DB / KAFKA / HTTP / SSH / …" />
            </mat-form-field>

            <!-- Data-kind conditional -->
            <mat-form-field appearance="outline" *ngIf="form.get('fieldKind')?.value === 'Data'"
                            class="conditional-field">
                <mat-label>Data type</mat-label>
                <mat-select formControlName="dataType">
                    <mat-option value="string">string</mat-option>
                    <mat-option value="number">number</mat-option>
                    <mat-option value="enum">enum</mat-option>
                    <mat-option value="userdata">userdata</mat-option>
                </mat-select>
            </mat-form-field>

            <!-- enum-only conditional -->
            <mat-form-field appearance="outline"
                            *ngIf="form.get('fieldKind')?.value === 'Data' && form.get('dataType')?.value === 'enum'"
                            class="conditional-field full-width">
                <mat-label>Enum values (JSON array)</mat-label>
                <input matInput formControlName="enumValuesJson"
                       placeholder='["ACTIVE","DISABLED"]' />
                <mat-hint>JSON array of strings, e.g. ["A","B","C"]</mat-hint>
            </mat-form-field>

            <!-- userdata-only conditional -->
            <ng-container *ngIf="form.get('fieldKind')?.value === 'Data' && form.get('dataType')?.value === 'userdata'">
                <mat-form-field appearance="outline" class="conditional-field">
                    <mat-label>UserData type</mat-label>
                    <input matInput formControlName="userdataType" placeholder="e.g. NE" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="conditional-field full-width">
                    <mat-label>Attribute filter (JSON, optional)</mat-label>
                    <input matInput formControlName="userdataAttrFilterJson"
                           [placeholder]="USERDATA_FILTER_PLACEHOLDER" />
                    <mat-hint>JSON object narrowing the picker, e.g. &#123;"VENDOR":"CISCO"&#125;</mat-hint>
                </mat-form-field>
            </ng-container>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description (optional)</mat-label>
                <textarea matInput formControlName="description" rows="2"
                          placeholder="Mat-hint on input forms, hint on result viewer"></textarea>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Default value JSON (optional)</mat-label>
                <input matInput formControlName="defaultValueJson"
                       placeholder='"" or 0 or ["x"]' />
            </mat-form-field>
        </form>
    </div>

    <div class="bw-footer">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-flat-button color="primary" [disabled]="form.invalid || saving()"
                (click)="save()">
            {{ data.mode === 'create' ? 'Create' : 'Save' }}
        </button>
    </div>
</div>
`,
    styles: [`
.bw-body { padding: 16px 24px; max-height: 70vh; overflow: auto; }
.bw-footer {
    display: flex; justify-content: flex-end; gap: 8px;
    padding: 12px 24px; border-top: 1px solid var(--border-divider);
}
.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;
}
.full-width { grid-column: 1 / -1; }
.checkbox-row { display: flex; align-items: center; padding: 8px 0; }
.conditional-field { background: var(--bg-surface-hover); border-radius: 6px; }
.mono { font-family: var(--font-mono, monospace); font-size: 12px; }
`],
})
export class CapabilityFieldDialogComponent implements OnInit {

    data: CapabilityFieldDialogData = inject(MAT_DIALOG_DATA);
    private ref: MatDialogRef<CapabilityFieldDialogComponent> = inject(MatDialogRef);
    private fb: FormBuilder = inject(FormBuilder);
    private fieldService: CapabilityFieldControllerService = inject(CapabilityFieldControllerService);
    private permissionChecker: PermissionCheckerService = inject(PermissionCheckerService);

    // Avoid the literal '{' / '}' in template placeholder strings — Angular
    // parses them as ICU openers and rejects them. Bind via TS property instead.
    readonly USERDATA_FILTER_PLACEHOLDER = '{"VENDOR":"CISCO"}';

    form: FormGroup = this.fb.group({
        fieldName:               ['', [Validators.required, Validators.maxLength(64)]],
        fieldRole:               ['INPUT', Validators.required],
        groupName:               [''],
        fieldKind:               ['Data', Validators.required],
        cardinality:             ['single', Validators.required],
        required:                [false],
        fieldOrder:              [0],
        description:             [''],
        defaultValueJson:        [''],
        connectorType:           [''],
        dataType:                ['string'],
        enumValuesJson:          [''],
        userdataType:            [''],
        userdataAttrFilterJson:  [''],
    });

    saving = signal(false);

    ngOnInit(): void {
        if (this.data.field) {
            const f = this.data.field;
            this.form.patchValue({
                fieldName:               f.fieldName,
                fieldRole:               f.fieldRole,
                groupName:               f.groupName  ?? '',
                fieldKind:               f.fieldKind,
                cardinality:             f.cardinality,
                required:                f.required ?? false,
                fieldOrder:              f.fieldOrder ?? 0,
                description:             f.description ?? '',
                defaultValueJson:        f.defaultValueJson ?? '',
                connectorType:           f.connectorType ?? '',
                dataType:                f.dataType ?? 'string',
                enumValuesJson:          f.enumValuesJson ?? '',
                userdataType:            f.userdataType ?? '',
                userdataAttrFilterJson:  f.userdataAttrFilterJson ?? '',
            });
            this.form.get('fieldName')?.disable();
            this.form.get('fieldRole')?.disable();
        }
    }

    cancel(): void { this.ref.close(null); }

    save(): void {
        if (this.form.invalid) return;
        if (!this.permissionChecker.hasAccessToUpdateCapability()) { return; }
        this.saving.set(true);
        const v = this.form.getRawValue();
        const role = v.fieldRole as 'INPUT' | 'OUTPUT';
        const body: CapabilityFieldUpsertRequest = {
            fieldKind:               v.fieldKind,
            cardinality:             v.cardinality,
            required:                !!v.required,
            groupName:               v.groupName  || undefined,
            fieldOrder:              Number(v.fieldOrder) || 0,
            description:             v.description || undefined,
            defaultValueJson:        v.defaultValueJson || undefined,
            connectorType:           v.fieldKind === 'Connector' ? (v.connectorType || undefined) : undefined,
            dataType:                v.fieldKind === 'Data' ? v.dataType : undefined,
            enumValuesJson:          v.fieldKind === 'Data' && v.dataType === 'enum'     ? (v.enumValuesJson || undefined) : undefined,
            userdataType:            v.fieldKind === 'Data' && v.dataType === 'userdata' ? (v.userdataType || undefined) : undefined,
            userdataAttrFilterJson:  v.fieldKind === 'Data' && v.dataType === 'userdata' ? (v.userdataAttrFilterJson || undefined) : undefined,
        };
        const obs = this.data.mode === 'create'
            ? this.fieldService.create(this.data.code, this.data.version, role, v.fieldName, body)
            : this.fieldService.update1(this.data.code, this.data.version, role, v.fieldName, body);
        obs.subscribe({
            next:  () => { this.saving.set(false); this.ref.close(true); },
            error: (e: any) => { this.saving.set(false); alert(`Save failed: ${typeof e === 'string' ? e : JSON.stringify(e)}`); },
        });
    }
}
